import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeaturedAnalysisBySlug } from '../hooks/useFeaturedAnalyses';
import { 
  EnhancedAnalysis, EngagementMetrics, ErrorBoundaryState, 
  NewsArticle, NewsAnalysisRelation, LearningProgress, AvailableAction,
  UserJourneyStage
} from '../types/enhancedAnalysis';

// 组件导入
import HeroSection from '../components/analysis/HeroSection';
import NewsContextSection from '../components/analysis/NewsContextSection';
import ContentNavigation from '../components/analysis/ContentNavigation';
import AnalysisContent from '../components/analysis/AnalysisContent';
import LearningPathSection from '../components/analysis/LearningPathSection';
import MethodologyShowcase from '../components/analysis/MethodologyShowcase';
import InteractiveLearning from '../components/analysis/InteractiveLearning';
import SidebarStats from '../components/analysis/SidebarStats';
// 移除FloatingShareTools组件导入
import ProgressiveActionGuide from '../components/analysis/ProgressiveActionGuide';
import ActionZone from '../components/analysis/ActionZone';
import SEOHead from '../components/common/SEOHead';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorBoundary from '../components/common/ErrorBoundary';

// 服务导入
import { EngagementTracker } from '../services/engagementTracker';
import { AnalyticsService } from '../services/analyticsService';
import { NewsArticleService } from '../services/newsArticleService';

interface OptimizedAnalysisPageState {
  analysis: EnhancedAnalysis | null;
  newsArticle: NewsArticle | null;
  newsAnalysisRelation: NewsAnalysisRelation | null;
  readingProgress: number;
  userEngagement: EngagementMetrics | null;
  showMethodology: boolean;
  activeSection: string;
  isLoading: boolean;
  error: ErrorBoundaryState | null;
  newsExpanded: boolean;
  currentUserStage: UserJourneyStage;
  learningProgress: LearningProgress;
}

const OptimizedAnalysisPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: baseAnalysis, error: fetchError, isLoading: isFetching } = useFeaturedAnalysisBySlug(slug || '');
  
  // 状态管理
  const [state, setState] = useState<OptimizedAnalysisPageState>({
    analysis: null,
    newsArticle: null,
    newsAnalysisRelation: null,
    readingProgress: 0,
    userEngagement: null,
    showMethodology: false,
    activeSection: 'introduction',
    isLoading: true,
    error: null,
    newsExpanded: false,
    currentUserStage: 'reading',
    learningProgress: {
      hasViewedMethodology: false,
      hasDownloadedTemplate: false,
      hasStartedPractice: false,
      completionPercentage: 0,
      nextRecommendedAction: {
        id: 'view_methodology',
        title: '查看分析方法',
        description: '了解这篇分析使用的方法论',
        type: 'read',
        priority: 'high',
        estimatedTime: 10,
        requiresAuth: false
      },
      learningPath: []
    }
  });

  // Refs
  const contentRef = useRef<HTMLDivElement>(null);
  const engagementTracker = useRef<EngagementTracker | null>(null);
  const startTime = useRef<Date>(new Date());

  // 初始化参与度追踪
  useEffect(() => {
    if (baseAnalysis && !engagementTracker.current) {
      engagementTracker.current = new EngagementTracker({
        analysisId: baseAnalysis.id,
        userId: user?.id,
        sessionId: generateSessionId(),
        startTime: startTime.current
      });
    }

    return () => {
      if (engagementTracker.current) {
        engagementTracker.current.destroy();
      }
    };
  }, [baseAnalysis, user]);

  // 增强分析数据
  const enhanceAnalysisData = useCallback((analysis: any): EnhancedAnalysis => {
    return {
      ...analysis,
      author: {
        id: 'system',
        name: '思维训练平台',
        title: '深度分析专家',
        expertise: ['商业分析', '战略思维', '数据洞察']
      },
      readingTime: Math.ceil((analysis.full_article || analysis.preview_content).length / 300),
      difficultyLevel: 'intermediate' as const,
      wordCount: (analysis.full_article || analysis.preview_content).length,
      lastUpdated: analysis.updated_at || analysis.created_at,
      keyInsights: extractKeyInsights(analysis),
      practicalTips: extractPracticalTips(analysis),
      methodology: generateMethodology(analysis),
      socialMetrics: generateSocialMetrics(),
      seoMetadata: generateSEOMetadata(analysis),
      relatedCases: [],
      recommendedActions: generateRecommendedActions(analysis),
      tableOfContents: generateTableOfContents(analysis),
      sections: generateContentSections(analysis)
    };
  }, []);

  // 处理数据加载
  useEffect(() => {
    if (isFetching) {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
    } else if (fetchError) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: {
          hasError: true,
          errorType: 'network',
          errorMessage: '加载分析内容失败',
          retryCount: 0,
          canRecover: true
        }
      }));
    } else if (baseAnalysis) {
      const enhancedAnalysis = enhanceAnalysisData(baseAnalysis);
      setState(prev => ({
        ...prev,
        analysis: enhancedAnalysis,
        isLoading: false,
        error: null
      }));
      
      // 直接使用公开接口返回的新闻数据，避免重复调用
      if (baseAnalysis?.insight_news_article) {
        const newsData = baseAnalysis.insight_news_article;
        const transformedNewsArticle = {
          id: baseAnalysis.id?.toString() || 'unknown',
          title: newsData.title || '',
          summary: newsData.summary || '',
          content: newsData.content || '',
          category: newsData.category || '',
          originalUrl: newsData.original_url,
          publishTime: new Date(),
          readingTime: Math.ceil((newsData.content?.length || 0) / 300),
          source: {
            id: 'public-source',
            name: '官方新闻',
            credibilityScore: 0.9,
            type: 'official' as const
          },
          tags: [],
          status: 1,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        const newsAnalysisRelation = NewsArticleService.generateNewsAnalysisRelation(
          transformedNewsArticle, 
          baseAnalysis?.title || ''
        );
        
        setState(prev => ({
          ...prev,
          newsArticle: transformedNewsArticle,
          newsAnalysisRelation
        }));
      }
    }
  }, [baseAnalysis, fetchError, isFetching, enhanceAnalysisData]);

  // 注释：已优化为直接使用公开接口返回的新闻数据
  // const loadNewsArticle = async (analysisId: string) => {
  //   // 此函数已被移除，避免重复调用 news-admin-manage 接口
  // };

  // 滚动监听 - 更新阅读进度
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min((scrollTop / docHeight) * 100, 100);

      setState(prev => ({ ...prev, readingProgress: progress }));

      // 追踪参与度
      if (engagementTracker.current) {
        engagementTracker.current.updateProgress(progress);
      }

      // 更新活跃章节
      updateActiveSection(scrollTop);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 更新活跃章节
  const updateActiveSection = useCallback((scrollTop: number) => {
    const sections = document.querySelectorAll('[data-section]');
    let activeSection = 'introduction';

    sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      if (rect.top <= 100 && rect.bottom > 100) {
        activeSection = section.getAttribute('data-section') || 'introduction';
      }
    });

    setState(prev => ({ ...prev, activeSection }));
  }, []);

  // 移除分享处理器

  const handleBookmark = useCallback(() => {
    if (!state.analysis) return;

    // 实现收藏逻辑
    AnalyticsService.track({
      category: 'engagement',
      action: 'bookmark',
      analysisId: state.analysis.id,
      sessionId: engagementTracker.current?.getSessionId() || '',
      timestamp: new Date()
    });
  }, [state.analysis]);

  const handleMethodologyToggle = useCallback(() => {
    setState(prev => ({ ...prev, showMethodology: !prev.showMethodology }));
    
    if (engagementTracker.current) {
      engagementTracker.current.trackMethodologyView();
    }
  }, []);

  const handleSectionNavigation = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleRetry = useCallback(() => {
    setState(prev => ({
      ...prev,
      error: prev.error ? { ...prev.error, retryCount: prev.error.retryCount + 1 } : null,
      isLoading: true
    }));
    
    // 重新加载数据的逻辑
    window.location.reload();
  }, []);

  // 新闻相关事件处理器
  const handleNewsToggle = useCallback(() => {
    setState(prev => ({ ...prev, newsExpanded: !prev.newsExpanded }));
    // 移除分析服务追踪
  }, []);

  const handleViewOriginal = useCallback(() => {
    if (state.newsArticle?.originalUrl) {
      window.open(state.newsArticle.originalUrl, '_blank');
    }
  }, [state.newsArticle]);

  // 学习路径事件处理器
  const handleStartLearning = useCallback(() => {
    setState(prev => ({
      ...prev,
      learningProgress: {
        ...prev.learningProgress,
        hasViewedMethodology: true
      },
      showMethodology: true
    }));
    
    if (engagementTracker.current) {
      engagementTracker.current.trackMethodologyView();
    }
  }, []);

  const handleTryTemplate = useCallback(() => {
    setState(prev => ({
      ...prev,
      learningProgress: {
        ...prev.learningProgress,
        hasDownloadedTemplate: true,
        hasStartedPractice: true
      }
    }));
    
    navigate('/training');
  }, [navigate]);

  const handleViewResources = useCallback(() => {
    // 实现查看资源逻辑
    console.log('View resources');
  }, []);

  // 渐进式操作引导
  const handleActionSelect = useCallback((action: AvailableAction) => {
    switch (action.id) {
      case 'start_practice':
        handleTryTemplate();
        break;
      case 'view_methodology':
        handleStartLearning();
        break;
      case 'register':
        navigate('/');
        break;
      default:
        console.log('Action selected:', action);
    }
  }, [handleTryTemplate, handleStartLearning, navigate]);

  const handleActionDismiss = useCallback(() => {
    // 实现操作引导关闭逻辑
    console.log('Action guide dismissed');
  }, []);

  // 移除分享处理器

  // 转化区域处理器
  const handleContinueLearning = useCallback(() => {
    handleStartLearning();
  }, [handleStartLearning]);

  const handleExploreMore = useCallback(() => {
    navigate('/analyses');
  }, [navigate]);

  // 错误状态渲染
  if (state.error?.hasError) {
    return (
      <ErrorBoundary>
        <div>抱歉，无法加载分析内容。</div>
      </ErrorBoundary>
    );
  }

  // 加载状态渲染
  if (state.isLoading || !state.analysis) {
    return <LoadingSpinner message="正在加载深度分析..." />;
  }

  const { analysis } = state;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO优化 */}
      <SEOHead
        title={analysis.seoMetadata.title}
        description={analysis.seoMetadata.description}
        keywords={analysis.seoMetadata.keywords.join(', ')}
        newsArticle={{
          headline: analysis.title,
          description: analysis.summary,
          author: {
            name: analysis.author.name,
            url: `${window.location.origin}/author/${analysis.author.id}`
          },
          publisher: {
            name: '思维训练平台',
            url: window.location.origin,
            logo: `${window.location.origin}/logo.png`
          },
          datePublished: analysis.created_at,
          url: window.location.href
        }}
        creativeWork={{
          name: analysis.title,
          description: analysis.summary,
          author: {
            name: analysis.author.name,
            url: `${window.location.origin}/author/${analysis.author.id}`
          },
          publisher: {
            name: '思维训练平台',
            url: window.location.origin,
            logo: `${window.location.origin}/logo.png`
          },
          dateCreated: analysis.created_at,
          url: window.location.href
        }}
      />

      {/* 英雄区域 */}
      <HeroSection
        analysis={analysis}
        onBookmark={handleBookmark}
        onStartReading={() => handleSectionNavigation('content')}
      />

      {/* 主要内容区域 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* 主内容区域 */}
          <div className="lg:col-span-3" ref={contentRef}>
            {/* 新闻背景区域 */}
            {state.newsArticle && state.newsAnalysisRelation && (
              <NewsContextSection
                newsArticle={state.newsArticle}
                newsAnalysisRelation={state.newsAnalysisRelation}
                isExpanded={state.newsExpanded}
                onToggleExpand={handleNewsToggle}
                onViewOriginal={handleViewOriginal}
              />
            )}

            {/* 内容导航 */}
            <ContentNavigation
              tableOfContents={analysis.tableOfContents}
              activeSection={state.activeSection}
              readingProgress={state.readingProgress}
              onSectionClick={handleSectionNavigation}
            />

            {/* 分析内容 */}
            <AnalysisContent
              analysis={analysis}
              activeSection={state.activeSection}
            />

            {/* 学习路径区域 */}
            <LearningPathSection
              analysis={analysis}
              userProgress={state.learningProgress}
              onStartLearning={handleStartLearning}
              onTryTemplate={handleTryTemplate}
              onViewResources={handleViewResources}
            />

            {/* 方法论展示 */}
            {state.showMethodology && (
              <MethodologyShowcase
                methodology={analysis.methodology}
                onDownloadTemplate={() => {}}
                onTryAnalysis={() => navigate('/training')}
              />
            )}

            {/* 互动学习区域 */}
            <InteractiveLearning
              analysis={analysis}
              onStartPractice={() => navigate('/training')}
              onViewThinkingProcess={handleMethodologyToggle}
            />

            {/* 转化区域 */}
            <ActionZone
              analysis={analysis}
              newsArticle={state.newsArticle || undefined}
              userEngagement={state.userEngagement || {
                sessionId: '',
                analysisId: analysis.id,
                readingProgress: state.readingProgress,
                timeSpent: 0,
                scrollDepth: state.readingProgress,
                readingSpeed: 0,
                sectionsViewed: [state.activeSection],
                methodologyViewed: state.showMethodology,
                bookmarked: false,
                liked: false,
                commented: false,
                practiceStarted: state.learningProgress.hasStartedPractice,
                templateDownloaded: state.learningProgress.hasDownloadedTemplate,
                relatedCasesViewed: 0,
                resourcesAccessed: [],
                registrationPrompted: false,
                registrationCompleted: !!user,
                conversionValue: 0,
                startTime: new Date(),
                lastActiveTime: new Date(),
                deviceType: 'desktop'
              }}
              user={user}
              onRegister={() => navigate('/')}
              onContinueLearning={handleContinueLearning}
              onExploreMore={handleExploreMore}
              readingProgress={state.readingProgress}
            />
          </div>

          {/* 侧边栏 */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* 统计信息 */}
              <SidebarStats
                analysis={analysis}
                readingProgress={state.readingProgress}
              />

              {/* 移除浮动分享工具 */}
            </div>
          </div>
        </div>
      </div>

      {/* 渐进式操作引导 */}
      <ProgressiveActionGuide
        currentStage={state.currentUserStage}
        readingProgress={state.readingProgress}
        engagementMetrics={state.userEngagement || {
          sessionId: '',
          analysisId: analysis.id,
          readingProgress: state.readingProgress,
          timeSpent: 0,
          scrollDepth: state.readingProgress,
          readingSpeed: 0,
          sectionsViewed: [state.activeSection],
          methodologyViewed: state.showMethodology,
          bookmarked: false,
          liked: false,
          commented: false,
          practiceStarted: state.learningProgress.hasStartedPractice,
          templateDownloaded: state.learningProgress.hasDownloadedTemplate,
          relatedCasesViewed: 0,
          resourcesAccessed: [],
          registrationPrompted: false,
          registrationCompleted: !!user,
          conversionValue: 0,
          startTime: new Date(),
          lastActiveTime: new Date(),
          deviceType: 'desktop'
        }}
        availableActions={[
          {
            id: 'view_methodology',
            title: '查看分析方法',
            description: '了解这篇分析使用的方法论',
            type: 'primary',
            timing: 'after_reading',
            position: 'floating',
            requiresAuth: false,
            estimatedTime: 10
          },
          {
            id: 'start_practice',
            title: '开始实践',
            description: '使用相同方法分析其他案例',
            type: 'secondary',
            timing: 'on_engagement',
            position: 'floating',
            requiresAuth: true,
            estimatedTime: 30
          }
        ]}
        onActionSelect={handleActionSelect}
        onDismiss={handleActionDismiss}
      />
    </div>
  );
};

// 辅助函数
function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

function extractKeyInsights(analysis: any) {
  // 从内容中提取关键洞察
  return [
    {
      id: '1',
      title: '核心洞察',
      description: analysis.summary,
      importance: 'high' as const,
      category: analysis.category,
      shareableQuote: analysis.summary.slice(0, 100) + '...',
      actionable: true
    }
  ];
}

function extractPracticalTips(analysis: any) {
  return [
    {
      id: '1',
      title: '实践建议',
      description: '基于分析结果的实用建议',
      actionable: true,
      difficulty: 'intermediate' as const,
      estimatedTime: 15,
      category: '实践应用'
    }
  ];
}

function generateMethodology(analysis: any) {
  return {
    id: 'swot',
    framework: 'SWOT',
    name: 'SWOT分析法',
    description: '从优势、劣势、机会、威胁四个维度进行分析',
    steps: [
      {
        id: '1',
        title: '识别优势',
        description: '分析内在的积极因素',
        example: '技术领先、品牌知名度高',
        tips: ['客观评估', '数据支撑'],
        order: 1
      }
    ],
    relatedResources: [],
    estimatedTime: 30,
    difficulty: 'intermediate' as const
  };
}

function generateSocialMetrics() {
  return {
    views: Math.floor(Math.random() * 10000) + 1000,
    shares: Math.floor(Math.random() * 500) + 50,
    likes: Math.floor(Math.random() * 200) + 20,
    comments: Math.floor(Math.random() * 50) + 5,
    bookmarks: Math.floor(Math.random() * 100) + 10,
    engagementRate: Math.random() * 0.1 + 0.05,
    sharesByPlatform: {
      wechat: Math.floor(Math.random() * 200),
      weibo: Math.floor(Math.random() * 150),
      linkedin: Math.floor(Math.random() * 100),
      twitter: Math.floor(Math.random() * 80),
      other: Math.floor(Math.random() * 70)
    },
    trending: Math.random() > 0.7
  };
}

function generateSEOMetadata(analysis: any, newsArticle?: NewsArticle) {
  // 结合新闻信息优化SEO
  const baseTitle = analysis.title;
  const newsContext = newsArticle ? ` - 基于"${newsArticle.title}"的深度分析` : '';
  const optimizedTitle = `${baseTitle}${newsContext} | LeapWard`;
  
  // 优化描述，包含新闻背景
  let optimizedDescription = analysis.summary.slice(0, 100);
  if (newsArticle) {
    optimizedDescription = `针对"${newsArticle.title}"的深度分析：${analysis.summary.slice(0, 80)}...`;
  }
  
  // 扩展关键词
  const keywords = [analysis.category, '深度分析', '思维训练'];
  if (newsArticle) {
    keywords.push('新闻分析', newsArticle.category, newsArticle.source.name);
  }
  keywords.push('SWOT分析', '商业分析', '战略思维');
  
  return {
    title: optimizedTitle.slice(0, 60), // 控制标题长度
    description: optimizedDescription.slice(0, 160), // 控制描述长度
    keywords,
    canonicalUrl: window.location.href,
    ogImage: newsArticle?.thumbnail || `${window.location.origin}/og-image.png`,
    structuredData: {
      type: 'Article' as const,
      headline: baseTitle,
      description: optimizedDescription,
      author: {
        id: 'system',
        name: '思维训练平台',
        title: '深度分析专家',
        expertise: ['商业分析', '战略思维', '新闻分析']
      },
      datePublished: analysis.created_at,
      dateModified: analysis.updated_at || analysis.created_at,
      publisher: {
        name: 'LeapWard',
        logo: `${window.location.origin}/logo.png`
      },
      mainEntityOfPage: window.location.href,
      // 新增新闻相关结构化数据
      ...(newsArticle && {
        about: {
          '@type': 'NewsArticle',
          headline: newsArticle.title,
          datePublished: newsArticle.publishTime.toISOString(),
          publisher: {
            '@type': 'Organization',
            name: newsArticle.source.name
          }
        }
      })
    },
    breadcrumbs: [
      { name: '首页', url: '/' },
      { name: '案例分析', url: '/analyses' },
      ...(newsArticle ? [{ name: '新闻分析', url: '/news-analysis' }] : []),
      { name: analysis.title, url: window.location.href }
    ]
  };
}

function generateRecommendedActions(analysis: any) {
  return [
    {
      id: '1',
      title: '尝试分析',
      description: '使用相同的方法论分析其他案例',
      type: 'practice' as const,
      priority: 'high' as const,
      estimatedTime: 30,
      requiresAuth: true
    }
  ];
}

function generateTableOfContents(analysis: any) {
  return [
    {
      id: 'introduction',
      title: '背景介绍',
      level: 1,
      anchor: 'introduction'
    },
    {
      id: 'analysis',
      title: '深度分析',
      level: 1,
      anchor: 'analysis'
    },
    {
      id: 'insights',
      title: '关键洞察',
      level: 1,
      anchor: 'insights'
    },
    {
      id: 'conclusion',
      title: '结论启示',
      level: 1,
      anchor: 'conclusion'
    }
  ];
}

function generateContentSections(analysis: any) {
  const content = analysis.full_article || analysis.preview_content;
  return [
    {
      id: 'introduction',
      title: '背景介绍',
      content: content.slice(0, Math.floor(content.length * 0.25)),
      type: 'introduction' as const,
      order: 1,
      estimatedReadingTime: 2,
      keyPoints: ['背景概述', '问题定义']
    },
    {
      id: 'analysis',
      title: '深度分析',
      content: content.slice(Math.floor(content.length * 0.25), Math.floor(content.length * 0.75)),
      type: 'analysis' as const,
      order: 2,
      estimatedReadingTime: 5,
      keyPoints: ['核心分析', '数据支撑']
    },
    {
      id: 'insights',
      title: '关键洞察',
      content: content.slice(Math.floor(content.length * 0.75)),
      type: 'insights' as const,
      order: 3,
      estimatedReadingTime: 2,
      keyPoints: ['关键发现', '实践建议']
    }
  ];
}

export default OptimizedAnalysisPage;